import './index.css'
import * as Yup from 'yup';
import Header from '../Header/Header';
import { useNavigate } from 'react-router-dom';
import { Loader, SuccessModal } from '@ohif/ui';
import { customStyle, formatTimeFromMinutes } from '@custom/Utils/utils';
import { Formik, Form, ErrorMessage } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme } from '../../ui/src/contextProviders';
import { AppDispatch, RootState } from '@custom/store/store';
import React, { useState, useEffect, useCallback } from 'react';
import getCaseTiming from '@custom/rtk-apis/Setting/getCaseTiming';
import addCaseTiming from '@custom/rtk-apis/Setting/addCaseTiming';
import { _commonUrls, _pacsUrls } from '@custom/Utils/constants/apiUrls';
import updateCaseTiming from '@custom/rtk-apis/Setting/updateCaseTiming';
import { clearGetCaseTimingState } from '@custom/store/slices/Setting/getCaseTimingSlice';
import { clearUpdateCaseTimingState } from '@custom/store/slices/Setting/updateCaseTimingSlice';

// Helper function to convert minutes to HH:MM format
const minutesToHM = (minutes: number): string => {
    if (!minutes || isNaN(minutes)) return '00:00';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return [
        hours.toString().padStart(2, '0'),
        mins.toString().padStart(2, '0')
    ].join(':');
};

// Helper function to convert HH:MM to minutes
const hmToMinutes = (hm: string): number => {
    if (!hm) return 0;
    const [hours, minutes] = hm.split(':').map(Number);
    return hours * 60 + minutes;
};



const CaseTimingSettings = () => {
    const isDarkMode = useTheme();
    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();
    const getCaseTimingResponse = useSelector((state: RootState) => state.getCaseTimingResponse);
    const addCaseTimingResponse = useSelector((state: RootState) => state.addCaseTimingResponse);
    const updateCaseTimingResponse = useSelector((state: RootState) => state.updateCaseTimingResponse);
    const role = sessionStorage.getItem('role') || localStorage.getItem('role');
    const [formValues, setFormValues] = useState({
        routine_time: '00:00',
        priority_time: '00:00',
        critical_time: '00:00',
        ...(role === 'Superadmin'
            ? {
                rejection_routine_time: '00:00',
                rejection_priority_time: '00:00',
                rejection_critical_time: '00:00'
            }
            : {})
    });
    const [caseTimingId, setCaseTimingId] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string>('');
    const [isSuccessModalOpen, setIsSuccessModalOpen] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);


    const timeField = (label) =>
        Yup.string()
            .required(`${label} is required`)
            .test('is-valid-format', 'Invalid time format (HH:MM)', value => {
                if (!value) return false;
                return /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/.test(value);
            })
            .test('is-greater-than-zero', 'Time must be greater than 00:00', value => {
                return hmToMinutes(value || '00:00') > 0;
            });

    const validationSchema = Yup.object().shape({
        routine_time: timeField('Routine'),
        priority_time: timeField('Priority'),
        critical_time: timeField('Critical'),
        ...(role === 'Superadmin'
            ? {
                rejection_routine_time: timeField('Rejection Routine'),
                rejection_priority_time: timeField('Rejection Priority'),
                rejection_critical_time: timeField('Rejection Critical'),
            }
            : {

            }),
    });

    const fetchCaseTiming = useCallback(() => {
        setLoading(true);
        dispatch(getCaseTiming());
    }, [dispatch]);

    useEffect(() => {
        fetchCaseTiming();
    }, [fetchCaseTiming]);

    useEffect(() => {
        if (addCaseTimingResponse.status === "SUCCESS" || updateCaseTimingResponse.status === "SUCCESS") {
            setLoading(false);
            setSuccessMessage(addCaseTimingResponse.msg || updateCaseTimingResponse.msg);
            setIsSuccessModalOpen(true);
            setTimeout(() => {
                fetchCaseTiming();
            }, 1500);
        } else {
            setLoading(false);
        }
    }, [addCaseTimingResponse.status, updateCaseTimingResponse.status, fetchCaseTiming]);

    useEffect(() => {
        if (getCaseTimingResponse.status === "SUCCESS") {
            const data = getCaseTimingResponse.result[0];

            const newFormValues = {
                routine_time: minutesToHM(data.routine_time),
                priority_time: minutesToHM(data.priority_time),
                critical_time: minutesToHM(data.critical_time),
                ...(role === 'Superadmin'
                    ? {
                        rejection_routine_time: minutesToHM(data.rejection_routine_time),
                        rejection_priority_time: minutesToHM(data.rejection_priority_time),
                        rejection_critical_time: minutesToHM(data.rejection_critical_time)
                    }
                    : {
                    })
            };

            // Only update if values have changed
            if (JSON.stringify(formValues) !== JSON.stringify(newFormValues)) {
                setFormValues(newFormValues);
            }

            setCaseTimingId(data._id);
            setLoading(false);
        }

        if (getCaseTimingResponse.status === "FAILED") {
            setLoading(false);
        }
    }, [getCaseTimingResponse]);

    const handleSave = (values: any) => {
        setLoading(true);
        const hospitalId = sessionStorage.getItem('hospitalId') || localStorage.getItem('hospitalId');

        if (!hospitalId) {
            console.error("Hospital ID is missing.");
            setLoading(false);
            return;
        }

        const formattedValues = {
            hospitalId,
            routine_time: hmToMinutes(values.routine_time),
            priority_time: hmToMinutes(values.priority_time),
            critical_time: hmToMinutes(values.critical_time),
            ...(role === 'Superadmin'
                ? {
                    rejection_routine_time: hmToMinutes(values.rejection_routine_time),
                    rejection_priority_time: hmToMinutes(values.rejection_priority_time),
                    rejection_critical_time: hmToMinutes(values.rejection_critical_time)
                }
                : {
                })
        };

        console.log("Formatted values: ", values.routine_time, formattedValues);

        if (caseTimingId) {
            dispatch(updateCaseTiming({ ...formattedValues, _id: caseTimingId }));
        } else {
            dispatch(addCaseTiming(formattedValues));
        }
    };

    const handleCancel = () => {
        navigate('/dashboard');
    }

    return (
        <>
            <div className='page page__full' style={isDarkMode?.isDarkMode ? { background: '#090c29' } : customStyle.mainBackground}>
                <Header isBack={false} title="Case Timing" />
                <div className='page__body'>
                    <div className='details'>
                        <div className='form form--innersec'>
                            <section className='form__section'>
                                <Formik
                                    enableReinitialize
                                    initialValues={formValues}
                                    validationSchema={validationSchema}
                                    onSubmit={handleSave}
                                >
                                    {({ values, handleChange, handleBlur, touched, errors }) => (
                                        <Form>
                                            <h1 className='form__section__title'>Case Timing Details</h1>
                                            <div className='formRow formRow__3'>
                                                <div className='formCol'>
                                                    <div className='form__Field'>
                                                        <label className='form__label' htmlFor='routine_time'>
                                                            Routine (HH:MM)<span className='required-asterisk'>*</span>
                                                        </label>
                                                        <input
                                                            type="text"
                                                            id="routine_time"
                                                            name="routine_time"
                                                            value={values.routine_time}
                                                            onChange={handleChange}
                                                            onBlur={handleBlur}
                                                            className='form__input form__input--sm'
                                                            placeholder="HH:MM"
                                                        />
                                                        <ErrorMessage name='routine_time' component='div' className='error text-error' />
                                                    </div>
                                                </div>
                                                <div className='formCol'>
                                                    <div className='form__Field'>
                                                        <label className='form__label' htmlFor='priority_time'>
                                                            Priority (HH:MM)<span className='required-asterisk'>*</span>
                                                        </label>
                                                        <input
                                                            type="text"
                                                            id="priority_time"
                                                            name="priority_time"
                                                            value={values.priority_time}
                                                            onChange={handleChange}
                                                            onBlur={handleBlur}
                                                            className='form__input form__input--sm'
                                                            placeholder="HH:MM"
                                                        />
                                                        <ErrorMessage name="priority_time" component="div" className="error text-error" />
                                                    </div>
                                                </div>
                                                <div className='formCol'>
                                                    <div className='form__Field'>
                                                        <label className='form__label' htmlFor='critical_time'>
                                                            Critical (HH:MM) <span className='required-asterisk'>*</span>
                                                        </label>
                                                        <input
                                                            type="text"
                                                            id="critical_time"
                                                            name="critical_time"
                                                            value={values.critical_time}
                                                            onChange={handleChange}
                                                            onBlur={handleBlur}
                                                            className='form__input form__input--sm'
                                                            placeholder="HH:MM"
                                                        />
                                                        <ErrorMessage name='critical_time' component='div' className='error text-error' />
                                                    </div>
                                                </div>
                                            </div>
                                            {
                                                role === 'Superadmin' && (
                                                    <h1 className='form__section__title' style={{ marginTop: '3rem' }}>Auto Rejection Details</h1>

                                                )
                                            }
                                            {
                                                role === 'Superadmin' && (
                                                    <div className="formRow formRow__3">
                                                        <div className='formCol'>
                                                            <div className='form__Field'>
                                                                <label className='form__label' htmlFor='rejection_routine_time'>
                                                                    Routine (HH:MM) <span className='required-asterisk'>*</span>
                                                                </label>
                                                                <input
                                                                    type="text"
                                                                    id="rejection_routine_time"
                                                                    name="rejection_routine_time"
                                                                    value={values.rejection_routine_time}
                                                                    onChange={handleChange}
                                                                    onBlur={handleBlur}
                                                                    className='form__input form__input--sm'
                                                                    placeholder="HH:MM"
                                                                />
                                                                <ErrorMessage name='rejection_routine_time' component='div' className='error text-error' />
                                                            </div>
                                                        </div>
                                                        <div className='formCol'>
                                                            <div className='form__Field'>
                                                                <label className='form__label' htmlFor='rejection_priority_time'>
                                                                    Priority (HH:MM) <span className='required-asterisk'>*</span>
                                                                </label>
                                                                <input
                                                                    type="text"
                                                                    id="rejection_priority_time"
                                                                    name="rejection_priority_time"
                                                                    value={values.rejection_priority_time}
                                                                    onChange={handleChange}
                                                                    onBlur={handleBlur}
                                                                    className='form__input form__input--sm'
                                                                    placeholder="HH:MM"
                                                                />
                                                                <ErrorMessage name="rejection_priority_time" component="div" className="error text-error" />
                                                            </div>
                                                        </div>
                                                        <div className='formCol'>
                                                            <div className='form__Field'>
                                                                <label className='form__label' htmlFor='rejection_critical_time'>
                                                                    Critical (HH:MM) <span className='required-asterisk'>*</span>
                                                                </label>
                                                                <input
                                                                    type="text"
                                                                    id="rejection_critical_time"
                                                                    name="rejection_critical_time"
                                                                    value={values.rejection_critical_time}
                                                                    onChange={handleChange}
                                                                    onBlur={handleBlur}
                                                                    className='form__input form__input--sm'
                                                                    placeholder="HH:MM"
                                                                />
                                                                <ErrorMessage name='rejection_critical_time' component='div' className='error text-error' />
                                                            </div>
                                                        </div>
                                                    </div>
                                                )
                                            }


                                            <div className='btn__group'>
                                                <button className='btn btn__primary' type='submit'>
                                                    {caseTimingId ? "Update" : "Save"}
                                                </button>
                                                <button
                                                    className='btn btn__white'
                                                    onClick={handleCancel}
                                                >
                                                    Cancel
                                                </button>
                                            </div>
                                        </Form>
                                    )}
                                </Formik>
                            </section>
                        </div>
                    </div>
                </div>
            </div>

            <SuccessModal
                isSuccessModalOpen={isSuccessModalOpen}
                title={successMessage}
                handleCloseModal={() => {
                    setIsSuccessModalOpen(false);
                    dispatch(clearUpdateCaseTimingState());
                    dispatch(clearGetCaseTimingState());
                }}
            />
            <Loader isLoaderOpen={loading} />
        </>
    );
};

export default CaseTimingSettings;
